/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {LabelValueRow} from './LabelValueRow';

export interface SpreaderMeasurement {
  value: string;
  unit?: string;
}

export interface SpreaderGranularInfoProps {
  materialName?: string;
  mode?: string;
  total?: SpreaderMeasurement;
}

const useStyles = makeStyles(() => ({
  container : {
    boxSizing       : 'border-box',
    display         : 'flex',
    width           : '100%',
    flexDirection   : 'column',
    gap             : '8px',
    padding         : '16px',
    backgroundColor : dlsColor('white'),
    border          : `0.5px solid ${dlsColor('grey7')}`,
    borderRadius    : '8px'
  },
  divider : {
    width     : '100%',
    height    : 0,
    border    : 'none',
    borderTop : `1px solid ${dlsColor('grey7')}`,
    margin    : 0
  }
}));

/**
 * SpreaderGranularInfo renders the Spreader Type 1 Granular Info block -
 * Material Name and Mode (both plain rows), a divider, then the Granular
 * Total (rendered heavier per Figma via `LabelValueRow`'s `emphasized`
 * prop). Each row is only rendered when its field is defined - `materialName`
 * or `mode` alone can render without the other, and the divider itself only
 * appears when `total` is present alongside at least one of them, so no
 * lone divider is ever left floating with nothing above or below it. The
 * whole block renders nothing when none of the three fields are provided.
 *
 * Per the Figma mockup, a field whose value is legitimately "N/A" or "0"
 * still renders that value - callers only omit a row (by leaving the
 * corresponding field `undefined`) when the measurement is truly absent.
 *
 * ```typescript
 *
 *   import {SpreaderGranularInfo} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderGranularInfo';
 *   ...
 *
 *   <SpreaderGranularInfo materialName='Salty'
 *     mode='Closed Loop'
 *     total={{value: '2,033,344', unit: 'mi'}} />
 *
 * ```
 *
 */
export const SpreaderGranularInfo = ({
  materialName,
  mode,
  total
}: SpreaderGranularInfoProps) => {
  const classes = useStyles();
  const hasUpperRows = materialName !== undefined || mode !== undefined;
  let block: React.ReactElement = null;

  if (hasUpperRows || total !== undefined) {
    block = (
      <div className={classes.container}>
        {materialName !== undefined && (
          <LabelValueRow label="Granular Material Name"
            value={materialName} />
        )}
        {mode !== undefined && (
          <LabelValueRow label="Granular Mode"
            value={mode} />
        )}
        {total !== undefined && hasUpperRows && (
          <hr className={classes.divider} />
        )}
        {total !== undefined && (
          <LabelValueRow label="Granular Total"
            value={total.value}
            unit={total.unit}
            emphasized />
        )}
      </div>
    );
  }

  return block;
};

export default memo(SpreaderGranularInfo);
